import { Injectable, inject } from '@angular/core';
import { Loader } from "@googlemaps/js-api-loader";
import { EnvironmentService } from '../env';
import { UrlService } from '../url/url.service';


@Injectable({ providedIn: 'root' })
export class GoogleApiService {
	private env = inject(EnvironmentService);

	public urlService = inject(UrlService);

	private _loader: Loader;

	constructor() {

		this._loader = new Loader({
			apiKey: this.env.getEnvironment().firebaseConfig.apiKey,
			version: "weekly",
			//...additionalOptions,
		});

	}

	public async getMaps(): Promise<google.maps.MapsLibrary> {
		if (!this._loader) throw new Error('getMaps() called before Loader was initialised.');
		const googleMaps = await this._loader.importLibrary('maps');
		return googleMaps;
	}


	public async getPlaces(): Promise<google.maps.PlacesLibrary> {
		if (!this._loader) throw new Error('getPlaces() called before Loader was initialised.');

		const googlePlaces = await this._loader.importLibrary('places');
		return googlePlaces;
	}

	public getGoogleMapPlaces() {
		return this.loadGoogleMapPlace();
	}

	private loadGoogleMapPlace(): Promise<typeof google.maps> {

		if (window.google && window.google.maps) {
			// Google Maps is already loaded, return it directly
			return Promise.resolve(window.google.maps);
		}

		const existingScript = document.getElementById("google-places-js-script");
		if (existingScript) {
			return new Promise((resolve) => {
				existingScript.addEventListener("load", () => resolve(window.google.maps));
			});
		}

		return this.setGoogleMapScript();
	}

	private setGoogleMapScript() {
		const script = document.createElement("script");
		script.type = "text/javascript";
		script.async = true;
		script.defer = true;
		script.id = "google-places-js-script";

		const callbackName = `GooglePlaces_cb_` + ((Math.random() * 1e9) >>> 0);
		script.src = this.getScriptSrc(callbackName);

		interface AppWindow {
			[name: string]: Function;
		}
		const appWindow: AppWindow = window as any;

		const promise = new Promise<typeof google.maps>((resolve, reject) => {
			appWindow[callbackName] = resolve;
			script.onerror = reject;
		});

		document.body.appendChild(script);

		return promise.then(() => google.maps);
	}

	private getScriptSrc(callback: string): string {
		interface QueryParams {
			[key: string]: string;
		}
		const query: QueryParams = {
			v: 'weekly',
			callback,
			key: this.env.getEnvironment().firebaseConfig.apiKey,
			libraries: 'places',
			loading: 'async',
			language: this.urlService.isSpanish() ? 'es' : 'en',
		};
		const params = Object.keys(query)
			.map((key) => `${key}=${query[key]}`)
			.join('&');
		return `//maps.googleapis.com/maps/api/js?${params}`;
	}

}